import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Building2, Save, Plus, Trash2, Star, Edit2,
  CreditCard, Globe, Hash, Calendar, FileText, Settings, Upload, Image, X, Loader,
  Users, User
} from 'lucide-react';
import { SellerProfile, InvoiceSettings as InvoiceSettingsType, SellerCreateData, InvoiceLanguage, Client, ClientCreateData } from '../../types';
import {
  getSellers, createSeller, updateSeller, deleteSeller, setDefaultSeller,
  getInvoiceSettings, updateInvoiceSettings, uploadSellerLogo, deleteSellerLogo
} from '../../api/sellers';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  fetchClients, createClient, updateClient, deleteClient, toggleClientFavorite
} from '../../store/invoiceSlice';
import KsefSettingsCard from './ksef/KsefSettingsCard';
import { useSubscription } from '../../hooks/useSubscription';
import { useConfirm } from '../ui/ConfirmDialog';
import Modal from '../ui/Modal';
import { sellerLogoUrl } from '../../utils/formatters';
import { Crown, Lock } from 'lucide-react';

const CURRENCIES = ['USD', 'EUR', 'GBP', 'PLN', 'RON', 'CAD', 'AUD'];
const LANGUAGES: { value: InvoiceLanguage; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'pl', label: 'Polski' },
  { value: 'ro', label: 'Romana' }
];

const InvoiceSettingsPage: React.FC = () => {
  // State
  const { isKsefEligible } = useSubscription();
  const confirm = useConfirm();
  const [sellers, setSellers] = useState<SellerProfile[]>([]);
  const [settings, setSettings] = useState<InvoiceSettingsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Seller modal state
  const [showSellerModal, setShowSellerModal] = useState(false);
  const [editingSeller, setEditingSeller] = useState<SellerProfile | null>(null);
  const [sellerForm, setSellerForm] = useState<SellerCreateData>({
    name: '',
    tax_id: '',
    email: '',
    phone: '',
    website: '',
    street: '',
    street_number: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    bank_name: '',
    bank_account_number: '',
    bank_swift: '',
    bank_iban: '',
    is_default: false
  });

  // Clients live in Redux because the invoice form's client dropdown reads the
  // same list - editing here keeps that picker in sync without a refetch.
  const dispatch = useAppDispatch();
  const clients = useAppSelector(state => state.invoices.clients);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientModal, setShowClientModal] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [clientForm, setClientForm] = useState<ClientCreateData>({
    name: '',
    is_business: true,
    email: '',
    phone: '',
    tax_id: '',
    street: '',
    street_number: '',
    city: '',
    postal_code: '',
    country: ''
  });

  // Logo upload state
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Settings form state
  const [settingsForm, setSettingsForm] = useState({
    invoice_number_prefix: 'INV',
    invoice_number_padding: 5,
    default_currency: 'USD',
    default_tax_rate: 0,
    default_payment_terms_days: 14,
    default_language: 'en' as InvoiceLanguage,
    default_secondary_language: '' as InvoiceLanguage | '',
    default_notes: '',
    default_terms: ''
  });

  // Load data
  useEffect(() => {
    loadData();
    dispatch(fetchClients({ favoritesFirst: true }));
  }, [dispatch]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [sellersData, settingsData] = await Promise.all([
        getSellers(),
        getInvoiceSettings()
      ]);
      setSellers(sellersData);
      setSettings(settingsData);

      // Update settings form
      setSettingsForm({
        invoice_number_prefix: settingsData.invoice_number_prefix || 'INV',
        invoice_number_padding: settingsData.invoice_number_padding || 5,
        default_currency: settingsData.default_currency || 'USD',
        default_tax_rate: settingsData.default_tax_rate || 0,
        default_payment_terms_days: settingsData.default_payment_terms_days || 14,
        default_language: (settingsData.default_language as InvoiceLanguage) || 'en',
        default_secondary_language: (settingsData.default_secondary_language as InvoiceLanguage) || '',
        default_notes: settingsData.default_notes || '',
        default_terms: settingsData.default_terms || ''
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  // Save settings
  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      // Convert empty string to undefined for secondary language
      const dataToSend = {
        ...settingsForm,
        default_secondary_language: settingsForm.default_secondary_language || undefined
      };
      await updateInvoiceSettings(dataToSend);
      setSuccess('Settings saved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Seller CRUD
  const handleCreateSeller = async () => {
    try {
      setSaving(true);
      setError(null);
      const newSeller = await createSeller(sellerForm);
      setSellers([...sellers, newSeller]);
      setShowSellerModal(false);
      resetSellerForm();
      setSuccess('Seller profile created');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to create seller');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateSeller = async () => {
    if (!editingSeller) return;
    try {
      setSaving(true);
      setError(null);
      const updated = await updateSeller(editingSeller.id, sellerForm);
      setSellers(sellers.map(s => s.id === updated.id ? updated : s));
      setShowSellerModal(false);
      setEditingSeller(null);
      resetSellerForm();
      setSuccess('Seller profile updated');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update seller');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSeller = async (sellerId: number) => {
    const seller = sellers.find(s => s.id === sellerId);
    const ok = await confirm({
      title: 'Delete this company profile?',
      message: (
        <>
          <b>{seller?.name ?? 'This profile'}</b> will be removed and can no longer be selected
          when creating invoices. Invoices already issued keep their own copy of these details.
        </>
      ),
      confirmLabel: 'Delete profile',
      destructive: true,
    });
    if (!ok) return;
    try {
      await deleteSeller(sellerId, true);
      setSellers(sellers.filter(s => s.id !== sellerId));
      setSuccess('Seller profile deleted');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to delete seller');
    }
  };

  const handleSetDefault = async (sellerId: number) => {
    try {
      await setDefaultSeller(sellerId);
      setSellers(sellers.map(s => ({
        ...s,
        is_default: s.id === sellerId
      })));
      setSuccess('Default seller updated');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to set default seller');
    }
  };

  const handleLogoUpload = async (sellerId: number, file: File) => {
    try {
      setUploadingLogo(true);
      const updatedSeller = await uploadSellerLogo(sellerId, file);
      setSellers(sellers.map(s => s.id === sellerId ? updatedSeller : s));
      if (editingSeller?.id === sellerId) {
        setEditingSeller(updatedSeller);
      }
      setSuccess('Logo uploaded successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoDelete = async (sellerId: number) => {
    try {
      setUploadingLogo(true);
      const updatedSeller = await deleteSellerLogo(sellerId);
      setSellers(sellers.map(s => s.id === sellerId ? updatedSeller : s));
      if (editingSeller?.id === sellerId) {
        setEditingSeller(updatedSeller);
      }
      setSuccess('Logo removed');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to remove logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const openEditSeller = (seller: SellerProfile) => {
    setEditingSeller(seller);
    setSellerForm({
      name: seller.name,
      tax_id: seller.tax_id || '',
      email: seller.email || '',
      phone: seller.phone || '',
      website: seller.website || '',
      street: seller.street || '',
      street_number: seller.street_number || '',
      city: seller.city || '',
      state: seller.state || '',
      postal_code: seller.postal_code || '',
      country: seller.country || '',
      bank_name: seller.bank_name || '',
      bank_account_number: seller.bank_account_number || '',
      bank_swift: seller.bank_swift || '',
      bank_iban: seller.bank_iban || '',
      is_default: seller.is_default
    });
    setShowSellerModal(true);
  };

  // Client CRUD
  const resetClientForm = () => {
    setClientForm({
      name: '',
      is_business: true,
      email: '',
      phone: '',
      tax_id: '',
      street: '',
      street_number: '',
      city: '',
      postal_code: '',
      country: ''
    });
  };

  const openAddClient = () => {
    resetClientForm();
    setEditingClient(null);
    setShowClientModal(true);
  };

  const openEditClient = (client: Client) => {
    setEditingClient(client);
    setClientForm({
      name: client.name,
      is_business: client.is_business,
      email: client.email || '',
      phone: client.phone || '',
      tax_id: client.tax_id || '',
      street: client.street || '',
      street_number: client.street_number || '',
      city: client.city || '',
      postal_code: client.postal_code || '',
      country: client.country || ''
    });
    setShowClientModal(true);
  };

  const handleSaveClient = async () => {
    if (!clientForm.name.trim()) return;
    try {
      setSaving(true);
      setError(null);
      if (editingClient) {
        await dispatch(updateClient({ id: editingClient.id, data: clientForm })).unwrap();
        setSuccess('Client updated. Invoices already issued keep their original details.');
      } else {
        await dispatch(createClient(clientForm)).unwrap();
        setSuccess('Client added');
      }
      setShowClientModal(false);
      setEditingClient(null);
      setTimeout(() => setSuccess(null), 4000);
    } catch (err: any) {
      setError(typeof err === 'string' ? err : err.message || 'Failed to save client');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClient = async (client: Client) => {
    const ok = await confirm({
      title: `Remove ${client.name}?`,
      message: (
        <>
          They will no longer appear when creating invoices. Existing invoices are unaffected —
          each one keeps its own copy of the buyer details.
        </>
      ),
      confirmLabel: 'Remove client',
      destructive: true,
    });
    if (!ok) return;
    try {
      setError(null);
      await dispatch(deleteClient(client.id)).unwrap();
      setSuccess(`${client.name} removed`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(typeof err === 'string' ? err : err.message || 'Failed to remove client');
    }
  };

  const handleToggleClientFavorite = async (client: Client) => {
    try {
      setError(null);
      await dispatch(toggleClientFavorite(client.id)).unwrap();
    } catch (err: any) {
      setError(typeof err === 'string' ? err : err.message || 'Failed to update favorite');
    }
  };

  const visibleClients = clients.filter(c => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return true;
    return [c.name, c.email, c.tax_id, c.city]
      .some(field => field?.toLowerCase().includes(q));
  });

  const resetSellerForm = () => {
    setSellerForm({
      name: '',
      tax_id: '',
      email: '',
      phone: '',
      website: '',
      street: '',
      street_number: '',
      city: '',
      state: '',
      postal_code: '',
      country: '',
      bank_name: '',
      bank_account_number: '',
      bank_swift: '',
      bank_iban: '',
      is_default: false
    });
  };

  // Single close path per modal, so Cancel, the X, Escape and a backdrop click
  // all leave the same state behind.
  const closeSellerModal = () => {
    setShowSellerModal(false);
    setEditingSeller(null);
    resetSellerForm();
  };

  const closeClientModal = () => {
    setShowClientModal(false);
    setEditingClient(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-coral-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link
            to="/created-invoices"
            className="p-2 hover:bg-stone-100 dark:hover:bg-coral-500/10 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-stone-600 dark:text-coral-400" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-stone-900 dark:text-white">Invoice Settings</h1>
            <p className="text-stone-500 dark:text-foreground-muted">Configure your invoice defaults and company profiles</p>
          </div>
        </div>
        <Settings className="w-8 h-8 text-coral-600 dark:text-coral-400" />
      </div>

      {/* Alerts */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg text-red-700 dark:text-red-300">
          {error}
        </div>
      )}
      {success && (
        <div className="mb-6 p-4 bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/20 rounded-lg text-green-700 dark:text-green-300">
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
        {/* Seller Profiles Section */}
        <div className="bg-white dark:bg-coral-500/5 rounded-xl shadow-sm border border-stone-200 dark:border-coral-500/20 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-coral-600 dark:text-coral-400" />
              <h2 className="text-lg font-semibold text-stone-900 dark:text-white">Company Profiles</h2>
            </div>
            <button
              onClick={() => {
                resetSellerForm();
                setEditingSeller(null);
                setShowSellerModal(true);
              }}
              className="flex items-center gap-1 px-3 py-1.5 bg-coral-600 text-white rounded-lg hover:bg-coral-700 text-sm"
            >
              <Plus className="w-4 h-4" />
              Add Company
            </button>
          </div>

          {sellers.length === 0 ? (
            <div className="text-center py-8 text-stone-500 dark:text-foreground-muted">
              <Building2 className="w-12 h-12 mx-auto mb-2 text-stone-300 dark:text-coral-500/30" />
              <p>No company profiles yet</p>
              <p className="text-sm">Add your company details to include on invoices</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sellers.map(seller => (
                <div
                  key={seller.id}
                  className={`p-4 rounded-lg border ${
                    seller.is_default
                      ? 'border-coral-300 dark:border-coral-500/40 bg-coral-50 dark:bg-coral-500/10'
                      : 'border-stone-200 dark:border-coral-500/20 bg-stone-50 dark:bg-coral-500/5'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      {/* Logo display */}
                      {seller.logo_path ? (
                        <img
                          src={sellerLogoUrl(seller.logo_path)}
                          alt={`${seller.name} logo`}
                          className="w-12 h-12 object-contain rounded border border-stone-200 dark:border-coral-500/20 bg-white dark:bg-coral-500/10"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded border border-stone-200 dark:border-coral-500/20 bg-white dark:bg-coral-500/10 flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-stone-300 dark:text-coral-500/30" />
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-stone-900 dark:text-white">{seller.name}</span>
                          {seller.is_default && (
                            <span className="px-2 py-0.5 bg-coral-100 dark:bg-coral-500/20 text-coral-700 dark:text-foreground-muted text-xs rounded-full">
                              Default
                            </span>
                          )}
                        </div>
                      {seller.tax_id && (
                        <p className="text-sm text-stone-600 dark:text-foreground-secondary">Tax ID: {seller.tax_id}</p>
                      )}
                      {seller.email && (
                        <p className="text-sm text-stone-500 dark:text-foreground-muted">{seller.email}</p>
                      )}
                      {seller.city && (
                        <p className="text-sm text-stone-500 dark:text-foreground-muted">
                          {[seller.city, seller.country].filter(Boolean).join(', ')}
                        </p>
                      )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {!seller.is_default && (
                        <button
                          onClick={() => handleSetDefault(seller.id)}
                          className="p-1.5 text-stone-400 dark:text-coral-400 hover:text-yellow-500 dark:hover:text-yellow-400 hover:bg-yellow-50 dark:hover:bg-yellow-500/10 rounded"
                          title="Set as default"
                        >
                          <Star className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => openEditSeller(seller)}
                        className="p-1.5 text-stone-400 dark:text-coral-400 hover:text-blue-500 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteSeller(seller.id)}
                        className="p-1.5 text-stone-400 dark:text-coral-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 rounded"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Clients Section */}
        <div className="bg-white dark:bg-coral-500/5 rounded-xl shadow-sm border border-stone-200 dark:border-coral-500/20 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5 text-coral-600 dark:text-coral-400" />
              <h2 className="text-lg font-semibold text-stone-900 dark:text-white">Clients</h2>
              {clients.length > 0 && (
                <span className="px-2 py-0.5 bg-stone-100 dark:bg-coral-500/20 text-stone-600 dark:text-foreground-muted text-xs rounded-full">
                  {clients.length}
                </span>
              )}
            </div>
            <button
              onClick={openAddClient}
              className="flex items-center gap-1 px-3 py-1.5 bg-coral-600 text-white rounded-lg hover:bg-coral-700 text-sm"
            >
              <Plus className="w-4 h-4" />
              Add Client
            </button>
          </div>

          {clients.length === 0 ? (
            <div className="text-center py-8 text-stone-500 dark:text-foreground-muted">
              <Users className="w-12 h-12 mx-auto mb-2 text-stone-300 dark:text-coral-500/30" />
              <p>No clients yet</p>
              <p className="text-sm">Add the people and companies you invoice</p>
            </div>
          ) : (
            <>
              {/* Keep the box while a term is active. Hiding it purely on
                  clients.length > 5 stranded users who deleted their way down to
                  five with a search term still applied: the list showed
                  "No clients match…" with no way to clear it. */}
              {(clients.length > 5 || clientSearch) && (
                <input
                  type="text"
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  placeholder="Search clients..."
                  className="w-full mb-3 px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500 focus:border-transparent"
                />
              )}
              <div className="space-y-3 max-h-[28rem] overflow-y-auto">
                {visibleClients.map(client => (
                  <div
                    key={client.id}
                    className={`p-4 rounded-lg border ${
                      client.is_favorite
                        ? 'border-coral-300 dark:border-coral-500/40 bg-coral-50 dark:bg-coral-500/10'
                        : 'border-stone-200 dark:border-coral-500/20 bg-stone-50 dark:bg-coral-500/5'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded border border-stone-200 dark:border-coral-500/20 bg-white dark:bg-coral-500/10 flex items-center justify-center">
                          {client.is_business ? (
                            <Building2 className="w-6 h-6 text-stone-300 dark:text-coral-500/30" />
                          ) : (
                            <User className="w-6 h-6 text-stone-300 dark:text-coral-500/30" />
                          )}
                        </div>
                        <div>
                          <span className="font-medium text-stone-900 dark:text-white">{client.name}</span>
                          {client.tax_id && (
                            <p className="text-sm text-stone-600 dark:text-foreground-secondary">Tax ID: {client.tax_id}</p>
                          )}
                          {client.email && (
                            <p className="text-sm text-stone-500 dark:text-foreground-muted">{client.email}</p>
                          )}
                          {(client.street || client.city) && (
                            <p className="text-sm text-stone-500 dark:text-foreground-muted">
                              {[
                                [client.street, client.street_number].filter(Boolean).join(' '),
                                client.postal_code,
                                client.city,
                                client.country
                              ].filter(Boolean).join(', ')}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleToggleClientFavorite(client)}
                          className={`p-1.5 rounded hover:bg-yellow-50 dark:hover:bg-yellow-500/10 ${
                            client.is_favorite
                              ? 'text-yellow-500 dark:text-yellow-400'
                              : 'text-stone-400 dark:text-coral-400 hover:text-yellow-500 dark:hover:text-yellow-400'
                          }`}
                          title={client.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
                        >
                          <Star className={`w-4 h-4 ${client.is_favorite ? 'fill-current' : ''}`} />
                        </button>
                        <button
                          onClick={() => openEditClient(client)}
                          className="p-1.5 text-stone-400 dark:text-coral-400 hover:text-blue-500 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClient(client)}
                          className="p-1.5 text-stone-400 dark:text-coral-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 rounded"
                          title="Remove"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {visibleClients.length === 0 && (
                  <p className="text-center py-4 text-sm text-stone-500 dark:text-foreground-muted">
                    No clients match "{clientSearch}"
                  </p>
                )}
              </div>
            </>
          )}
        </div>
        </div>

        {/* Invoice Defaults Section */}
        <div className="space-y-6">
          {/* Number Format */}
          <div className="bg-white dark:bg-coral-500/5 rounded-xl shadow-sm border border-stone-200 dark:border-coral-500/20 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Hash className="w-5 h-5 text-coral-600 dark:text-coral-400" />
              <h2 className="text-lg font-semibold text-stone-900 dark:text-white">Invoice Numbering</h2>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">
                  Prefix
                </label>
                <input
                  type="text"
                  value={settingsForm.invoice_number_prefix}
                  onChange={(e) => setSettingsForm({ ...settingsForm, invoice_number_prefix: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500 focus:border-transparent"
                  placeholder="INV"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">
                  Digits
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={settingsForm.invoice_number_padding}
                  onChange={(e) => setSettingsForm({ ...settingsForm, invoice_number_padding: parseInt(e.target.value) || 5 })}
                  className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500 focus:border-transparent"
                />
              </div>
            </div>
            <p className="text-sm text-stone-500 dark:text-foreground-muted mt-2">
              Preview: {settingsForm.invoice_number_prefix}-{'0'.repeat(settingsForm.invoice_number_padding - 1)}1
            </p>
          </div>

          {/* Currency & Tax */}
          <div className="bg-white dark:bg-coral-500/5 rounded-xl shadow-sm border border-stone-200 dark:border-coral-500/20 p-6">
            <div className="flex items-center gap-2 mb-4">
              <CreditCard className="w-5 h-5 text-coral-600 dark:text-coral-400" />
              <h2 className="text-lg font-semibold text-stone-900 dark:text-white">Currency & Tax</h2>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">
                  Default Currency
                </label>
                <select
                  value={settingsForm.default_currency}
                  onChange={(e) => setSettingsForm({ ...settingsForm, default_currency: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500 focus:border-transparent"
                >
                  {CURRENCIES.map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">
                  Default Tax Rate (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={settingsForm.default_tax_rate}
                  onChange={(e) => setSettingsForm({ ...settingsForm, default_tax_rate: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Payment & Language */}
          <div className="bg-white dark:bg-coral-500/5 rounded-xl shadow-sm border border-stone-200 dark:border-coral-500/20 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Globe className="w-5 h-5 text-coral-600 dark:text-coral-400" />
              <h2 className="text-lg font-semibold text-stone-900 dark:text-white">Payment & Language</h2>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">
                  Payment Terms (days)
                </label>
                <input
                  type="number"
                  min="0"
                  value={settingsForm.default_payment_terms_days}
                  onChange={(e) => setSettingsForm({ ...settingsForm, default_payment_terms_days: parseInt(e.target.value) || 14 })}
                  className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500 focus:border-transparent"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">
                    Primary Language
                  </label>
                  <select
                    value={settingsForm.default_language}
                    onChange={(e) => setSettingsForm({ ...settingsForm, default_language: e.target.value as InvoiceLanguage })}
                    className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500 focus:border-transparent"
                  >
                    {LANGUAGES.map(l => (
                      <option key={l.value} value={l.value}>{l.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">
                    Secondary Language (Bilingual)
                  </label>
                  <select
                    value={settingsForm.default_secondary_language}
                    onChange={(e) => setSettingsForm({ ...settingsForm, default_secondary_language: e.target.value as InvoiceLanguage | '' })}
                    className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500 focus:border-transparent"
                  >
                    <option value="">None (Single language)</option>
                    {LANGUAGES.filter(l => l.value !== settingsForm.default_language).map(l => (
                      <option key={l.value} value={l.value}>{l.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              {settingsForm.default_secondary_language && (
                <p className="text-sm text-stone-500 dark:text-foreground-muted">
                  Invoice labels will show as: Primary / Secondary (e.g., "Invoice / Faktura")
                </p>
              )}
            </div>
          </div>

          {/* Default Text */}
          <div className="bg-white dark:bg-coral-500/5 rounded-xl shadow-sm border border-stone-200 dark:border-coral-500/20 p-6">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-coral-600 dark:text-coral-400" />
              <h2 className="text-lg font-semibold text-stone-900 dark:text-white">Default Text</h2>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">
                  Default Notes
                </label>
                <textarea
                  value={settingsForm.default_notes}
                  onChange={(e) => setSettingsForm({ ...settingsForm, default_notes: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500 focus:border-transparent"
                  placeholder="Notes that appear on all invoices..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">
                  Default Terms & Conditions
                </label>
                <textarea
                  value={settingsForm.default_terms}
                  onChange={(e) => setSettingsForm({ ...settingsForm, default_terms: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500 focus:border-transparent"
                  placeholder="Terms and conditions..."
                />
              </div>
            </div>
          </div>

          {/* KSeF connection (Phase 2 — direct e-invoice submission) */}
          <div className="bg-white dark:bg-coral-500/5 rounded-xl shadow-sm border border-stone-200 dark:border-coral-500/20 p-6">
            {isKsefEligible ? (
              <KsefSettingsCard />
            ) : (
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 rounded-lg bg-coral-50 dark:bg-coral-500/10 border border-coral-200 dark:border-coral-500/30">
                <div className="p-3 rounded-lg bg-coral-100 dark:bg-coral-500/20 flex-shrink-0">
                  <Lock className="w-5 h-5 text-coral-600 dark:text-foreground-muted" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-base font-semibold text-stone-900 dark:text-white">KSeF e-invoicing</h3>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-purple-100 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-500/30">
                      <Crown className="w-3 h-3" /> Advanced
                    </span>
                  </div>
                  <p className="text-sm text-stone-600 dark:text-foreground-muted">
                    Submit invoices directly to Poland's KSeF system (Ministry of Finance). Available on the Advanced plan only.
                  </p>
                </div>
                <Link
                  to="/pricing"
                  className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-coral-600 hover:bg-coral-700 text-white rounded-lg font-medium text-sm transition-colors whitespace-nowrap"
                >
                  Upgrade to Advanced
                </Link>
              </div>
            )}
          </div>

          {/* Save Button */}
          <button
            onClick={handleSaveSettings}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-coral-600 text-white rounded-lg hover:bg-coral-700 disabled:opacity-50"
          >
            <Save className="w-5 h-5" />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      {/* Seller Modal */}
      {showSellerModal && (
        <Modal
          open
          onClose={closeSellerModal}
          size="lg"
          title={editingSeller ? 'Edit Company Profile' : 'Add Company Profile'}
          footer={
            <>
              <button
                onClick={closeSellerModal}
                className="px-4 py-2 text-stone-700 dark:text-foreground-muted hover:bg-stone-100 dark:hover:bg-coral-500/10 rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                onClick={editingSeller ? handleUpdateSeller : handleCreateSeller}
                disabled={!sellerForm.name || saving}
                className="px-4 py-2 bg-coral-600 text-white rounded-lg hover:bg-coral-700 disabled:opacity-50 text-sm"
              >
                {saving ? 'Saving...' : (editingSeller ? 'Update' : 'Create')}
              </button>
            </>
          }
        >
          <div>

            {/* Logo Upload Section - Only show when editing */}
            {editingSeller && (
              <div className="mb-6 p-4 bg-stone-50 dark:bg-coral-500/5 rounded-lg border border-stone-200 dark:border-coral-500/20">
                <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-3">Company Logo</label>
                <div className="flex items-center gap-4">
                  {editingSeller.logo_path ? (
                    <div className="relative">
                      <img
                        src={sellerLogoUrl(editingSeller.logo_path)}
                        alt="Company logo"
                        className="w-20 h-20 object-contain rounded-lg border border-stone-200 dark:border-coral-500/20 bg-white dark:bg-coral-500/10"
                      />
                      <button
                        type="button"
                        onClick={() => handleLogoDelete(editingSeller.id)}
                        disabled={uploadingLogo}
                        className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        title="Remove logo"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-20 h-20 rounded-lg border-2 border-dashed border-stone-300 dark:border-coral-500/30 flex items-center justify-center bg-white dark:bg-coral-500/10">
                      <Image className="w-8 h-8 text-stone-300 dark:text-coral-500/30" />
                    </div>
                  )}
                  <div className="flex-1">
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept=".png,.jpg,.jpeg,.gif,.webp"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file && editingSeller) {
                          handleLogoUpload(editingSeller.id, file);
                        }
                        e.target.value = '';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                      className="flex items-center gap-2 px-4 py-2 bg-coral-600 text-white rounded-lg hover:bg-coral-700 disabled:opacity-50"
                    >
                      {uploadingLogo ? (
                        <Loader className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                      {editingSeller.logo_path ? 'Change Logo' : 'Upload Logo'}
                    </button>
                    <p className="text-xs text-stone-500 dark:text-foreground-muted mt-2">
                      PNG, JPG, GIF or WebP. Max 2MB.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={sellerForm.name}
                  onChange={(e) => setSellerForm({ ...sellerForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">Tax ID / VAT</label>
                <input
                  type="text"
                  value={sellerForm.tax_id}
                  onChange={(e) => setSellerForm({ ...sellerForm, tax_id: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">Email</label>
                <input
                  type="email"
                  value={sellerForm.email}
                  onChange={(e) => setSellerForm({ ...sellerForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">Phone</label>
                <input
                  type="text"
                  value={sellerForm.phone}
                  onChange={(e) => setSellerForm({ ...sellerForm, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">Website</label>
                <input
                  type="text"
                  value={sellerForm.website}
                  onChange={(e) => setSellerForm({ ...sellerForm, website: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                />
              </div>

              <div className="col-span-2 border-t border-stone-200 dark:border-coral-500/20 pt-4 mt-2">
                <h4 className="text-sm font-medium text-stone-900 dark:text-white mb-3">Address</h4>
                <div className="grid grid-cols-4 gap-4">
                  <div className="col-span-3">
                    <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">Street</label>
                    <input
                      type="text"
                      value={sellerForm.street}
                      onChange={(e) => setSellerForm({ ...sellerForm, street: e.target.value })}
                      className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">Number</label>
                    <input
                      type="text"
                      value={sellerForm.street_number}
                      onChange={(e) => setSellerForm({ ...sellerForm, street_number: e.target.value })}
                      className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">City</label>
                    <input
                      type="text"
                      value={sellerForm.city}
                      onChange={(e) => setSellerForm({ ...sellerForm, city: e.target.value })}
                      className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">State</label>
                    <input
                      type="text"
                      value={sellerForm.state}
                      onChange={(e) => setSellerForm({ ...sellerForm, state: e.target.value })}
                      className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">Postal Code</label>
                    <input
                      type="text"
                      value={sellerForm.postal_code}
                      onChange={(e) => setSellerForm({ ...sellerForm, postal_code: e.target.value })}
                      className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">Country</label>
                    <input
                      type="text"
                      value={sellerForm.country}
                      onChange={(e) => setSellerForm({ ...sellerForm, country: e.target.value })}
                      className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                    />
                  </div>
                </div>
              </div>

              <div className="col-span-2 border-t border-stone-200 dark:border-coral-500/20 pt-4 mt-2">
                <h4 className="text-sm font-medium text-stone-900 dark:text-white mb-3">Bank Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">Bank Name</label>
                    <input
                      type="text"
                      value={sellerForm.bank_name}
                      onChange={(e) => setSellerForm({ ...sellerForm, bank_name: e.target.value })}
                      className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">Account Number</label>
                    <input
                      type="text"
                      value={sellerForm.bank_account_number}
                      onChange={(e) => setSellerForm({ ...sellerForm, bank_account_number: e.target.value })}
                      className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">IBAN</label>
                    <input
                      type="text"
                      value={sellerForm.bank_iban}
                      onChange={(e) => setSellerForm({ ...sellerForm, bank_iban: e.target.value })}
                      className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">SWIFT/BIC</label>
                    <input
                      type="text"
                      value={sellerForm.bank_swift}
                      onChange={(e) => setSellerForm({ ...sellerForm, bank_swift: e.target.value })}
                      className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Add / Edit Client modal */}
      {showClientModal && (
        <Modal
          open
          onClose={closeClientModal}
          size="lg"
          title={editingClient ? 'Edit Client' : 'Add Client'}
          description={
            editingClient
              ? 'Invoices you have already issued keep the details they were created with. '
                + 'Changes apply to new invoices only.'
              : undefined
          }
          footer={
            <>
              <button
                onClick={closeClientModal}
                className="px-4 py-2 text-stone-700 dark:text-foreground-muted hover:bg-stone-100 dark:hover:bg-coral-500/10 rounded-lg text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveClient}
                disabled={!clientForm.name.trim() || saving}
                className="px-4 py-2 bg-coral-600 text-white rounded-lg hover:bg-coral-700 disabled:opacity-50 text-sm"
              >
                {saving ? 'Saving...' : (editingClient ? 'Update' : 'Create')}
              </button>
            </>
          }
        >
          <div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-2">Client Type</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setClientForm({ ...clientForm, is_business: true })}
                  className={`flex-1 px-4 py-2 rounded-lg border text-sm ${
                    clientForm.is_business
                      ? 'border-coral-500 bg-coral-50 dark:bg-coral-500/20 text-coral-700 dark:text-white'
                      : 'border-stone-300 dark:border-coral-500/20 text-stone-600 dark:text-foreground-muted'
                  }`}
                >
                  Business
                </button>
                <button
                  type="button"
                  onClick={() => setClientForm({ ...clientForm, is_business: false })}
                  className={`flex-1 px-4 py-2 rounded-lg border text-sm ${
                    !clientForm.is_business
                      ? 'border-coral-500 bg-coral-50 dark:bg-coral-500/20 text-coral-700 dark:text-white'
                      : 'border-stone-300 dark:border-coral-500/20 text-stone-600 dark:text-foreground-muted'
                  }`}
                >
                  Individual
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">
                  {clientForm.is_business ? 'Company Name' : 'Full Name'} *
                </label>
                <input
                  type="text"
                  value={clientForm.name}
                  onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                />
              </div>
              {clientForm.is_business && (
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">Tax ID / VAT Number</label>
                  <input
                    type="text"
                    value={clientForm.tax_id}
                    onChange={(e) => setClientForm({ ...clientForm, tax_id: e.target.value })}
                    className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">Email</label>
                <input
                  type="email"
                  value={clientForm.email}
                  onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">Phone</label>
                <input
                  type="tel"
                  value={clientForm.phone}
                  onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">Street</label>
                <input
                  type="text"
                  value={clientForm.street}
                  onChange={(e) => setClientForm({ ...clientForm, street: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">Number</label>
                <input
                  type="text"
                  value={clientForm.street_number}
                  onChange={(e) => setClientForm({ ...clientForm, street_number: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">Postal Code</label>
                <input
                  type="text"
                  value={clientForm.postal_code}
                  onChange={(e) => setClientForm({ ...clientForm, postal_code: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">City</label>
                <input
                  type="text"
                  value={clientForm.city}
                  onChange={(e) => setClientForm({ ...clientForm, city: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-stone-700 dark:text-foreground-secondary mb-1">Country</label>
                <input
                  type="text"
                  value={clientForm.country}
                  onChange={(e) => setClientForm({ ...clientForm, country: e.target.value })}
                  className="w-full px-3 py-2 border border-stone-300 dark:border-coral-500/20 rounded-lg bg-white dark:bg-coral-500/10 text-stone-900 dark:text-white focus:ring-2 focus:ring-coral-500"
                />
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default InvoiceSettingsPage;
